import { useState } from "react";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { UserRoles } from "@/services/user-auth/user-roles";
import sprites from "@/shared/assets/sprite.svg";
import { EmailLink } from "@/shared/components/EmailLink";
import { SUPPORT_EMAIL } from "@/shared/constants/common";
import {
	bronzeGradientHalfTransparentBorderClassName,
	goldGradientHalfTransparentBorderClassName,
	platinumGradientHalfTransparentBorderClassName,
	silverGradientHalfTransparentBorderClassName,
} from "@/shared/constants/customStyleSets";
import type {
	PricingCardType,
	SubscriptionPeriod,
	SubscriptionPlan,
} from "../../../../types";

type Props = {
	card: PricingCardType;
	className?: string;
	handleSubmit: (data: {
		plan: SubscriptionPlan;
		period: SubscriptionPeriod;
	}) => void;
};

const SubscriptionPlanLabelMap: Record<SubscriptionPlan, string> = {
	[UserRoles.BRONZE]: "Bronze",
	[UserRoles.SILVER]: "Silver",
	[UserRoles.GOLD]: "Gold",
	[UserRoles.PLATINUM]: "Platinum",
};

const GRADIENT_BORDER_MAP: Record<SubscriptionPlan, string> = {
	[UserRoles.BRONZE]: "bronze-border",
	[UserRoles.SILVER]: "silver-border",
	[UserRoles.GOLD]: "gold-border",
	[UserRoles.PLATINUM]: "platinum-border",
} as const;

const GRADIENT_BUTTON_MAP: Record<SubscriptionPlan, string> = {
	[UserRoles.BRONZE]: bronzeGradientHalfTransparentBorderClassName,
	[UserRoles.SILVER]: silverGradientHalfTransparentBorderClassName,
	[UserRoles.GOLD]: goldGradientHalfTransparentBorderClassName,
	[UserRoles.PLATINUM]: platinumGradientHalfTransparentBorderClassName,
} as const;

const getGradientBorderAnimationClassName = (plan: SubscriptionPlan) =>
	GRADIENT_BORDER_MAP[plan] ?? "";

const getGradientButtonClassName = (plan: SubscriptionPlan) =>
	GRADIENT_BUTTON_MAP[plan] ?? "";

export const PricingCard: React.FC<Props> = (props) => {
	const { card, handleSubmit, ...otherProps } = props;
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function submitForm(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setIsLoading(true);

		try {
			handleSubmit({ plan: card.plan, period: card.period });
		} catch (_e) {
			setError("An error occurred while processing your request.");
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<form
			data-testid="pricing-card"
			className={cn(
				"max-w-[300px] min-w-[300px] w-full h-full flex flex-col items-center py-5 px-6 gap-4 rounded-2xl relative",
				getGradientBorderAnimationClassName(card.plan),
			)}
			onSubmit={submitForm}
			{...otherProps}
		>
			<p className="text-4xl font-medium font-afacad">
				{SubscriptionPlanLabelMap[card.plan]}
			</p>

			<div
				className={cn(
					"flex flex-col items-center w-full gap-1 flex-1",
					card.disabled && "transition-all duration-1000 group hover:gap-3",
				)}
			>
				<div className="flex justify-center items-center gap-0.5">
					<p className="text-4xl font-medium font-afacad">${card.price}</p>
					<p className="text-sm w-6 font-afacad">per {card.period}</p>
				</div>

				<div className="min-h-4 h-4">
					{card.period === "month" && (
						<p className="font-afacad text-xs">{card.additionalInfo}</p>
					)}
				</div>

				{card.disabled && (
					<p className="text-4xl font-medium font-afacad mb-1 group transition-all duration-1000 opacity-50 group-hover:opacity-100">
						Coming Soon
					</p>
				)}

				{!card?.disabled && (
					<Button
						type="submit"
						disabled={isLoading || card.isCurrentPlan}
						variant="outline"
						className={cn(
							getGradientButtonClassName(card.plan),
							"max-w-[180px] w-full h-7.5 rounded-lg mt-2",
						)}
					>
						Subscribe
					</Button>
				)}

				<ul
					className={cn(
						"list-inside flex flex-col gap-2 mt-1 w-[90%]",
						card.disabled &&
							"opacity-50 transition-all duration-1000 group-hover:opacity-100",
					)}
				>
					{card.options?.map((option) => (
						<li className="flex gap-1 items-center" key={option}>
							<svg
								aria-hidden="true"
								fill="#ffffff"
								className="min-w-4 w-4 h-4"
							>
								<use xlinkHref={`${sprites}#check-circle`} />
							</svg>

							<p className="text-[16px] font-afacad font-medium">{option}</p>
						</li>
					))}
				</ul>
			</div>

			{isLoading && <Spinner />}

			{error ? (
				<p className="text-red-500 text-lg absolute -bottom-12 left-0 right-0 text-center">
					Please try again later or contact <EmailLink email={SUPPORT_EMAIL} />.
				</p>
			) : null}
		</form>
	);
};
